<template>
    <section :style="{ background: sectionBackgroundColor }">
      <div class="relative py-10 pt-8 overflow-hidden lg:py-20 font-ww-regular">
        <div class="relative z-10 px-4 mx-auto max-w-screen-2xl">
          <div class="grid grid-cols-12 mb-6 xl:gap-x-4 md:gap-x-20">
            <div class="order-2 col-span-12 lg:col-span-7 lg:order-1">
              <h3
                class="hidden font-bold leading-none text-black h1 font-ww-medium lg:block text-balance"
                :style="{ color: titleTextColor }"
                v-html="titleText"
              ></h3>
              <div
                class="mt-4 h2 lg:h3 font-ww-regular lg:pr-24"
                :style="{ color: descriptionTextColor }"
                v-html="descriptionText"
              ></div>
  
              <div>
                <div
                  class="flex items-center mt-6 gap-x-2 lg:pl-6 lg:pr-8"
                  v-for="(item, index) in items"
                  :key="index"
                >
                  <div class="basis-12">
                    <nuxt-img
                      :src="item.imgSrc"
                      alt="stars"
                      class="object-cover mx-auto"
                      width="45"
                      height="43"
                      format="webp"
                      loading="lazy"
                      sizes="xs:40px sm:42px md:42px lg:45px xl:45px"
                    ></nuxt-img>
                  </div>
                  <div class="basis-full">
                    <h3
                      class="leading-6 text-left font h3 xl:h4 font-ww-regular"
                      :style="{ color: bulletTextColor }"
                      v-html="item.text"
                    ></h3>
                  </div>
                </div>
              </div>
            </div>
  
            <div class="flex flex-col col-span-12 lg:col-span-5 lg:order-2 lg:mt-6 md:mt-0">
              <h3
                class="mb-5 font-bold text-center h1 lg:hidden lg:mb-0"
                :style="{ color: titleTextColor }"
                v-html="titleText"
              ></h3>
              <nuxt-img
                sizes="xs:340px sm:382px md:544px lg:372px xl:400px 2xl:492px"
                :src="mainImageSrc"
                alt="person"
                class="w-full max-w-xl m-auto"
                width="592"
                height="448"
                format="webp"
                loading="lazy"
              ></nuxt-img>
            </div>
          </div>
  
          <div
            class="max-w-4xl mx-auto italic text-center h3 font-ww-regular"
            :style="{ color: footerTextColor }"
            v-html="footerText"
          ></div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  const props = defineProps({
    items: {
      type: Array,
      default: () => [
        {
          text: "<strong>Low-Acid, Stomach-Friendly</strong> – No bitterness, no burn. Just pure, smooth flavor.",
          imgSrc: "/assets/images/Star_icon.png",
        },
        {
          text: "<strong>Free from Harmful Toxins</strong> – Lab-tested and certified clean, so you can enjoy worry-free.",
          imgSrc: "/assets/images/Star_icon.png",
        },
        {
          text: "<strong>Single-Origin, High-Quality Beans</strong> – Each cup brings you the best of nature, right from sustainable farms.",
          imgSrc: "/assets/images/Star_icon.png",
        },
        {
          text: "<strong>Crafted with Patriotism in Every Batch</strong> – This coffee is dedicated to those who believe in the American spirit.",
          imgSrc: "/assets/images/Star_icon.png",
        },
      ],
    },
    sectionBackgroundColor: {
      type: String,
      default: "#f9f4f4",
    },
    titleText: {
      type: String,
      default: "Clean, Bold, Pure – The Taste of Freedom",
    },
    titleTextColor: {
      type: String,
      default: "black",
    },
    descriptionText: {
      type: String,
      default:
        "Our Patriot Coffee embodies the flavor of freedom. This blend is as Patriotic as it is special: Raspberries picked from the fields of central Ohio, blueberries harvested from the Lake Erie Coast, and white chocolate imported direct from Belgium. An absolutely perfect combination with a Grand Slam taste!",
    },
    descriptionTextColor: {
      type: String,
      default: "black",
    },
    bulletTextColor: {
      type: String,
      default: "black",
    },
    mainImageSrc: {
      type: String,
      default: "/assets/images/Benefits_image_-_coffee.png",
    },
    footerText: {
      type: String,
      default:
        "Crafted with Patriotism in Every Batch <br> This coffee is dedicated to those who believe in the American spirit.",
    },
    footerTextColor: {
      type: String,
      default: "black",
    },
  });
  
  defineBuilderComponent({
    friendlyName: "Description List With Image",
    inputs: [
      {
        name: "sectionBackgroundColor",
        type: "color",
        friendlyName: "Section Background Color",
        defaultValue: "#f9f4f4",
      },
      {
        name: "titleText",
        type: "richText",
        friendlyName: "Title Text",
        defaultValue: "Clean, Bold, Pure – The Taste of Freedom",
      },
      {
        name: "titleTextColor",
        type: "color",
        friendlyName: "Title Text Color",
        defaultValue: "black",
      },
      {
        name: "descriptionText",
        type: "richText",
        friendlyName: "Description Text",
        defaultValue:
          "Our Patriot Coffee embodies the flavor of freedom. This blend is as Patriotic as it is special: Raspberries picked from the fields of central Ohio, blueberries harvested from the Lake Erie Coast, and white chocolate imported direct from Belgium. An absolutely perfect combination with a Grand Slam taste!",
      },
      {
        name: "descriptionTextColor",
        type: "color",
        friendlyName: "Description Text Color",
        defaultValue: "black",
      },
      {
        name: "items",
        type: "list",
        friendlyName: "Bullet Items",
        subFields: [
          {
            name: "text",
            type: "richText",
            friendlyName: "Bullet Item Text",
            defaultValue: "",
          },
          {
            name: "imgSrc",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "svg", "webp"],
            friendlyName: "Bullet Icon Image Source",
            defaultValue: "/assets/images/Star_icon.png",
          },
        ],
        defaultValue: [
          {
            text: "<strong>Low-Acid, Stomach-Friendly</strong> – No bitterness, no burn. Just pure, smooth flavor.",
            imgSrc: "/assets/images/Star_icon.png",
          },
          {
            text: "<strong>Free from Harmful Toxins</strong> – Lab-tested and certified clean, so you can enjoy worry-free.",
            imgSrc: "/assets/images/Star_icon.png",
          },
          {
            text: "<strong>Single-Origin, High-Quality Beans</strong> – Each cup brings you the best of nature, right from sustainable farms.",
            imgSrc: "/assets/images/Star_icon.png",
          },
          {
            text: "<strong>Crafted with Patriotism in Every Batch</strong> – This coffee is dedicated to those who believe in the American spirit.",
            imgSrc: "/assets/images/Star_icon.png",
          },
        ],
      },
      {
        name: "bulletTextColor",
        type: "color",
        friendlyName: "Bullet Text Color",
        defaultValue: "black",
      },
      {
        name: "mainImageSrc",
        type: "file",
        allowedFileTypes: ["jpeg", "jpg", "png", "svg", "webp"],
        friendlyName: "Main Image Source",
        defaultValue: "/assets/images/Benefits_image_-_coffee.png",
      },
      {
        name: "footerText",
        type: "richText",
        friendlyName: "Footer Text",
        defaultValue:
          "Crafted with Patriotism in Every Batch <br> This coffee is dedicated to those who believe in the American spirit.",
      },
      {
        name: "footerTextColor",
        type: "color",
        friendlyName: "Footer Text Color",
        defaultValue: "black",
      },
    ],
  });
  </script>
  